import React, { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { acceptTeamInvitation, findUnclaimedTeams, claimTeam } from '../../lib/supabase'
import { BillingManagement } from '../BillingManagement'
import Header from '../Header'

export const UserDashboard: React.FC = () => {
  const { user, userTeams, userInvitations, refreshUserTeams, refreshInvitations, signOut } = useAuth()
  const [error, setError] = useState('')
  const [showBilling, setShowBilling] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null)
  const [unclaimedTeams, setUnclaimedTeams] = useState<any[]>([])
  const location = useLocation()
  const navigate = useNavigate()

  // Handle upgrade parameter from pricing page
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const upgradeParam = searchParams.get('upgrade')
    
    if (upgradeParam && userTeams && userTeams.length > 0) {
      // If user has teams, show billing for the first team with the selected plan
      setSelectedPlan(upgradeParam)
      setShowBilling(true)
      // Clean up URL
      navigate('/dashboard', { replace: true })
    }
  }, [location.search, userTeams, navigate])

  // Load unclaimed teams
  useEffect(() => {
    const loadUnclaimedTeams = async () => {
      try {
        const unclaimed = await findUnclaimedTeams()
        setUnclaimedTeams(unclaimed)
      } catch (error) {
        console.error('Error loading unclaimed teams:', error)
      }
    }

    if (user) {
      loadUnclaimedTeams()
    }
  }, [user])

  const handleClaimTeam = async (userRecordId: string, teamId: string, teamName: string) => {
    try {
      await claimTeam(userRecordId, teamId)
      // Refresh teams and unclaimed teams
      await refreshUserTeams()
      const unclaimed = await findUnclaimedTeams()
      setUnclaimedTeams(unclaimed)
    } catch (err: any) {
      setError(`Failed to claim team "${teamName}": ${err.message}`)
    }
  }

  const handleAcceptInvitation = async (token: string) => {
    try {
      await acceptTeamInvitation(token)
      await Promise.all([refreshUserTeams(), refreshInvitations()])
    } catch (err: any) {
      setError(err.message || 'Failed to accept invitation')
    }
  }

  const handleSignOut = async () => {
    try {
      await signOut()
    } catch (err) {
      console.error('Sign out error:', err)
    }
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-[#F2F4ED] to-[#E8EBE3]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <p className="mt-2 text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  const teams = userTeams || []
  const invitations = userInvitations || []

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-[#F2F4ED] to-[#E8EBE3] relative">
      {/* Subtle peaceful background elements */}
      <div className="absolute inset-0 overflow-hidden opacity-20">
        <div className="absolute top-20 right-20 w-64 h-64 bg-gradient-to-br from-[#F2F4ED]/60 to-[#E8EBE3]/60 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-80 h-80 bg-gradient-to-tr from-[#E8EBE3]/50 to-[#F2F4ED]/50 rounded-full blur-3xl"></div>
      </div>

      {/* Header with logo */}
      <Header 
        onLogout={handleSignOut}
        showTeamSwitcher={false}
      />

      {/* Main content */}
      <main className="relative z-10 max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="space-y-8">
          
          {/* Error message */}
          {error && (
            <div className="bg-red-50/80 backdrop-blur-sm border border-red-200 rounded-lg p-4 shadow-sm">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          {/* Welcome message */}
          <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-8 shadow-sm border border-white/40">
            <h1 className="text-3xl font-bold text-slate-800 mb-2">
              Welcome to SyncSimple
            </h1>
            <p className="text-slate-600 text-lg">
              Your Slack-integrated standup management dashboard. Teams are automatically created when you sign in through your Slack workspace.
            </p>
            <div className="mt-4 flex items-center space-x-2 text-sm text-slate-500">
              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              <span>Signed in as {user.email}</span>
            </div>
          </div>

          {/* Pending invitations */}
          {invitations.length > 0 && (
            <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 shadow-sm border border-white/40">
              <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center">
                <div className="w-2 h-2 bg-amber-400 rounded-full mr-3"></div>
                Pending Invitations
              </h2>
              <div className="space-y-3">
                {invitations.map((invitation) => (
                  <div key={invitation.id} className="bg-white/70 rounded-xl p-4 border border-white/50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white font-semibold">
                          {invitation.teams?.name?.charAt(0) || 'T'}
                        </div>
                        <div>
                          <div className="font-medium text-slate-800">
                            {invitation.teams?.name || 'Unknown Team'}
                          </div>
                          <div className="text-sm text-slate-500">
                            Invited by team admin
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleAcceptInvitation(invitation.token)}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm"
                      >
                        Accept
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Unclaimed teams */}
          {unclaimedTeams.length > 0 && (
            <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 shadow-sm border border-white/40">
              <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center">
                <div className="w-2 h-2 bg-blue-400 rounded-full mr-3"></div>
                Teams You Can Claim
              </h2>
              <div className="bg-blue-50/70 border border-blue-200 rounded-xl p-4 mb-4">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-medium text-blue-800">
                      Teams found that you can claim
                    </h3>
                    <p className="mt-1 text-sm text-blue-700">
                      These teams were created when you installed the SyncSimple app but weren't automatically linked to your account. You can claim them to access their dashboards.
                    </p>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                {unclaimedTeams.map((userRecord) => (
                  <div key={userRecord.id} className="bg-white/70 rounded-xl p-4 border border-white/50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center text-white font-semibold">
                          {userRecord.teams?.name?.charAt(0) || 'T'}
                        </div>
                        <div>
                          <div className="font-medium text-slate-800">
                            {userRecord.teams?.name || 'Unknown Team'}
                          </div>
                          <div className="text-sm text-slate-500">
                            Slack workspace • Created when you installed SyncSimple
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleClaimTeam(userRecord.id, userRecord.team_id!, userRecord.teams?.name || 'Unknown Team')}
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm"
                      >
                        Claim Team
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Billing Modal */}
          {showBilling && userTeams && userTeams.length > 0 && (
            <BillingManagement
              teamId={userTeams[0].team_id}
              userRole={userTeams[0].role}
              isOpen={showBilling}
              onClose={() => {
                setShowBilling(false)
                setSelectedPlan(null)
              }}
              initialPlan={selectedPlan}
            />
          )}

          {/* Teams list */}
          <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 shadow-sm border border-white/40">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-slate-800 flex items-center">
                <div className="w-2 h-2 bg-indigo-400 rounded-full mr-3"></div>
                Your Teams
              </h2>
            </div>

            {teams.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-slate-200 to-slate-300 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM9 9a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-slate-700 mb-2">No teams yet</h3>
                <p className="text-slate-500 mb-6">
                  Teams are automatically created when you sign in through your Slack workspace.
                </p>
                <div className="bg-blue-50/70 border border-blue-200 rounded-xl p-6 max-w-md mx-auto">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-blue-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="text-left">
                      <h4 className="font-medium text-blue-800 mb-2">
                        How to get started
                      </h4>
                      <p className="text-sm text-blue-700 mb-3">To create a team and start using SyncSimple:</p>
                      <ol className="text-sm text-blue-700 space-y-1">
                        <li className="flex items-start">
                          <span className="inline-block w-5 h-5 bg-blue-100 text-blue-600 rounded-full text-xs text-center leading-5 mr-2 mt-0.5 font-medium">1</span>
                          Make sure you're signed in through your Slack workspace
                        </li>
                        <li className="flex items-start">
                          <span className="inline-block w-5 h-5 bg-blue-100 text-blue-600 rounded-full text-xs text-center leading-5 mr-2 mt-0.5 font-medium">2</span>
                          Your team will be automatically created based on your Slack workspace
                        </li>
                        <li className="flex items-start">
                          <span className="inline-block w-5 h-5 bg-blue-100 text-blue-600 rounded-full text-xs text-center leading-5 mr-2 mt-0.5 font-medium">3</span>
                          If you don't see a team, try signing out and signing back in
                        </li>
                      </ol>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {teams.map((membership) => (
                  <Link
                    key={membership.id}
                    to={`/dashboard/team/${membership.team_id}`}
                    className="block group"
                  >
                    <div className="bg-white/70 hover:bg-white/90 rounded-xl p-4 border border-white/50 hover:border-white/70 transition-all duration-200 hover:shadow-md">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white font-semibold">
                            {membership.teams?.name?.charAt(0) || 'T'}
                          </div>
                          <div>
                            <div className="font-medium text-slate-800 group-hover:text-slate-900">
                              {membership.teams?.name || 'Unknown Team'}
                            </div>
                            <div className="text-sm text-slate-500">
                              {membership.role} • Joined {membership.joined_at ? new Date(membership.joined_at).toLocaleDateString() : 'Unknown'}
                            </div>
                          </div>
                        </div>
                        <div className="text-slate-400 group-hover:text-slate-600 transition-colors">
                          <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  )
} 